import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuroraBackground from '../components/ui/aurora-background';
import { motion } from 'framer-motion';
import { Logo } from '../components/ui/logo';
import GafnirLogo from '../components/ui/gafnir-logo';

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

const LandingPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showValidationError, setShowValidationError] = useState(false);
  const [lastSubmitTime, setLastSubmitTime] = useState<number>(0);
  const SUBMIT_COOLDOWN_MS = 30000; // 30 seconds cooldown

  const handleLanguageChange = (lang: 'en' | 'is') => {
    i18n.changeLanguage(lang);
  };

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setShowValidationError(false);

    // Check if enough time has passed since last submission
    const now = Date.now();
    if (now - lastSubmitTime < SUBMIT_COOLDOWN_MS) {
      setSubmitStatus('error');
      return;
    }

    if (!isValidEmail(email)) {
      setShowValidationError(true);
      return;
    }

    setIsSubmitting(true);
    setLastSubmitTime(now);

    try {
      const apiUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:8000/api/mailchimp/subscribe'
        : '/api/mailchimp/subscribe';
      
      console.log('Submitting to:', apiUrl);
      console.log('Email:', email);
        
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);

      if (!response.ok) {
        console.error('Error response:', data);
        throw new Error(data.detail || 'Subscription failed');
      }

      setSubmitStatus('success');
      setEmail('');
    } catch (error) {
      console.error('Submission error:', error);
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      }
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    {
      id: 1,
      title: t('features.1.title'),
      description: t('features.1.description'),
      icon: '🤖',
      delay: 0.2,
    },
    {
      id: 2,
      title: t('features.2.title'),
      description: t('features.2.description'),
      icon: '📄',
      delay: 0.4,
    },
    {
      id: 3,
      title: t('features.3.title'),
      description: t('features.3.description'),
      icon: '🏢',
      delay: 0.6,
    },
  ];

  return (
    <AuroraBackground>
      {/* Coming Soon Banner */}
      <div className="absolute top-0 left-0 z-20">
        <div className="bg-gradient-to-r from-amber-500 to-rose-500 text-white px-24 transform -rotate-45 -translate-x-20 translate-y-8 shadow-lg h-[60px] flex items-center justify-center">
          <p className="text-lg md:text-xl font-bold tracking-wider translate-y-[1px]">{t('banner.comingSoon')}</p>
        </div>
      </div>

      {/* Language Switcher */}
      <div className="absolute top-4 right-4 flex space-x-4 z-10">
        <button
          onClick={() => handleLanguageChange('en')}
          className={`text-sm font-medium transition px-3 py-1 rounded-md ${
            i18n.language === 'en' ? 'bg-white/10 text-white' : 'text-white/70 hover:text-white'
          }`}
        >
          English
        </button>
        <button
          onClick={() => handleLanguageChange('is')}
          className={`text-sm font-medium transition px-3 py-1 rounded-md ${
            i18n.language === 'is' ? 'bg-white/10 text-white' : 'text-white/70 hover:text-white'
          }`}
        >
          Íslenska
        </button>
      </div>

      <div className="min-h-screen flex flex-col">
        {/* Hero Section */}
        <div className="flex-1 container mx-auto px-4 flex items-center justify-center">
          <motion.div 
            className="text-center py-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex justify-center mb-8">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
              <GafnirLogo width={180} className="drop-shadow-2xl" />
              </motion.div>
            </div>
            <div className="flex items-center justify-center min-h-[120px] sm:min-h-[180px] mb-8 sm:mb-12">
              <h1 className="text-4xl sm:text-6xl md:text-9xl font-bold bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent px-4">
                {t('hero.title')}
              </h1>
            </div>
            <p className="text-lg sm:text-xl md:text-2xl text-white/80 mb-8 sm:mb-12 max-w-2xl mx-auto leading-relaxed px-4">
              {t('hero.subtitle')}
            </p>

            {/* Email Capture Form */}
            <motion.form 
              onSubmit={handleSubmit} 
              className="max-w-md mx-auto mb-16 relative z-50"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <p className="text-lg text-white/90 mb-4 font-medium">
                {t('form.betaNotification')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setShowValidationError(false);
                    setSubmitStatus('idle');
                  }}
                  placeholder={t('form.emailPlaceholder')}
                  className={`flex-1 h-12 px-4 rounded-lg bg-white/10 border ${
                    showValidationError ? 'border-red-500' : 'border-white/20'
                  } text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-blue-400`}
                  required
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="h-12 px-8 rounded-lg bg-blue-500 hover:bg-blue-400 hover:scale-105 text-white font-medium transition-all duration-200 disabled:opacity-50 hover:shadow-lg hover:shadow-blue-500/25 cursor-pointer"
                >
                  {isSubmitting ? t('form.submitting') : t('form.submit')}
                </button>
              </div>
              {showValidationError && (
                <motion.p 
                  className="mt-3 text-red-400"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {t('form.invalidEmail')}
                </motion.p>
              )}
              {submitStatus === 'success' && (
                <motion.p 
                  className="mt-3 text-green-400"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {t('form.success')}
                </motion.p>
              )}
              {submitStatus === 'error' && (
                <motion.p 
                  className="mt-3 text-red-400"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {t('form.error')}
                </motion.p>
              )}
            </motion.form>

            {/* Features Grid */}
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {features.map((feature) => (
                <motion.div
                  key={feature.id}
                  className="p-6 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: feature.delay }}
                >
                  <div className="text-4xl mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold mb-2 text-white">{feature.title}</h3>
                  <p className="text-white/70">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Footer */}
        <footer className="w-full text-center py-4 mt-auto">
          <div className="flex flex-col items-center space-y-2">
            <Logo className="opacity-50 hover:opacity-100 transition-opacity" width={150} />
            <p className="text-white/50">{t('footer.rights')}</p>
          </div>
        </footer>
      </div>
    </AuroraBackground>
  );
};

export default LandingPage; 